/* FulfillmentChangeRequest — UI primitives for bilateral-consent order changes.
 *
 * Two surfaces:
 *   <SellerChangeRequestModal>  — seller opens from AdjustInvoiceModal when
 *      the proposed adjustment is a MATERIAL change (short-ship / substitute /
 *      mixed). Captures reason + proposed_items + posts to backend.
 *   <BuyerChangeRequestBanner>  — buyer sees this when there's a pending CRQ
 *      on one of their orders. Embeds the full decision UI (Accept / Decline /
 *      Cancel order) right inside the banner.
 *
 * Both components are pure presentational — backend calls happen via the
 * `api` client. Backend enforces all the business rules (price-up rejection,
 * one-pending-at-a-time, audit logging, refund on accept, etc.).
 */
import { useEffect, useState } from "react";
import {
  AlertTriangle, Package, Replace, Check, X, Loader2, Clock, ShieldX, RotateCcw,
} from "lucide-react";
import api, { money } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";

// ─────────────────────────────────────────────────────────────────────────────
// Seller-side modal — initiate a Fulfillment Change Request
// ─────────────────────────────────────────────────────────────────────────────
export function SellerChangeRequestModal({ open, onClose, order, invoice, onSent }) {
  // Local edit copy of the items — seller adjusts qty/sku here
  const [items, setItems] = useState([]);
  const [kind, setKind]   = useState("short_ship");
  const [reason, setReason] = useState("");
  const [busy, setBusy]     = useState(false);
  const [error, setError]   = useState(null);

  useEffect(() => {
    if (open && order) {
      setItems((order.items || []).map(it => ({
        sku:        it.sku || "",
        name:       it.name || "",
        qty:        Number(it.qty) || 0,
        unit_price: Number(it.unit_price || it.price) || 0,
        original_sku: it.sku || "",     // remember for substitute detection
        original_qty: Number(it.qty) || 0,
      })));
      setReason("");
      setError(null);
      setKind("short_ship");
    }
  }, [open, order?.id]);

  // Derived: total + delta + auto-detected kind
  const originalTotal = Number(invoice?.amount || order?.total || 0);
  const proposedTotal = items.reduce((s, it) => s + (it.qty || 0) * (it.unit_price || 0), 0);
  const refundDelta   = +(originalTotal - proposedTotal).toFixed(2);
  const hasSku        = items.some(it => it.sku !== it.original_sku);
  const hasQtyDrop    = items.some(it => it.qty < it.original_qty);
  const detectedKind  = hasSku && hasQtyDrop ? "mixed" : hasSku ? "substitute" : "short_ship";

  useEffect(() => { setKind(detectedKind); }, [detectedKind]);

  const exceeds = proposedTotal > originalTotal + 0.005;

  const send = async () => {
    if (!reason.trim() || reason.trim().length < 10) {
      setError("Please add a clear reason (at least 10 characters) for the buyer.");
      return;
    }
    if (exceeds) {
      setError("Total cannot exceed the original invoice. Price increases are not allowed post-acceptance.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post(`/seller/orders/${order.id}/change-request`, {
        order_id: order.id,
        kind,
        reason: reason.trim(),
        proposed_items: items.map(it => ({
          sku:        it.sku || null,
          name:       it.name,
          qty:        Number(it.qty) || 0,
          unit_price: Number(it.unit_price) || 0,
          substitute_for_sku: it.sku !== it.original_sku ? it.original_sku : null,
        })),
      });
      toast.success("Change request sent. Buyer has 7 days to respond.");
      onSent?.(data);
      onClose?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(typeof d === "string" ? d : (d?.message || "Couldn't send change request"));
    } finally { setBusy(false); }
  };

  if (!open || !order) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-2xl" data-testid="seller-crq-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 display text-xl">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            Request fulfillment change
            <span className="text-[11px] mono uppercase tracking-widest text-foreground/45 ml-2">
              Order {order.code || order.id?.slice(0, 8)}
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
          <div className="rounded-lg border border-amber-300 bg-amber-50/50 p-3 text-[12px] text-amber-900 flex items-start gap-2">
            <ShieldX className="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              <strong>Buyer must approve this change.</strong> The buyer paid for a specific spec, so material changes (short-ship, SKU substitution) require their explicit consent before you ship.
              <br />
              <span className="text-[11.5px]"><strong>Price cannot increase.</strong> If pricing needs to genuinely change, void the invoice and re-issue under the validity flow instead.</span>
            </div>
          </div>

          {/* Items editor */}
          <div className="rounded-xl border border-border bg-white p-4">
            <div className="text-[10px] mono uppercase tracking-widest text-foreground/55 mb-3">Proposed line items</div>
            <table className="w-full text-[12.5px]">
              <thead className="text-[10px] mono uppercase tracking-wider text-foreground/55">
                <tr>
                  <th className="text-left pb-1">SKU</th>
                  <th className="text-left pb-1">Item</th>
                  <th className="text-right pb-1">Qty</th>
                  <th className="text-right pb-1">Unit price</th>
                  <th className="text-right pb-1">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => {
                  const skuChanged = it.sku !== it.original_sku;
                  const qtyDropped = it.qty < it.original_qty;
                  return (
                    <tr key={i} className={skuChanged || qtyDropped ? "bg-amber-50/40" : ""}>
                      <td className="py-1.5 pr-2">
                        <Input value={it.sku || ""}
                               onChange={(e) => setItems(arr => arr.map((x, ix) => ix === i ? { ...x, sku: e.target.value } : x))}
                               className="h-8 font-mono text-[11.5px]"
                               data-testid={`crq-line-sku-${i}`}/>
                        {skuChanged && <div className="text-[9.5px] text-amber-700 mt-0.5 mono uppercase tracking-wider">Substituted</div>}
                      </td>
                      <td className="py-1.5 pr-2">
                        <Input value={it.name}
                               onChange={(e) => setItems(arr => arr.map((x, ix) => ix === i ? { ...x, name: e.target.value } : x))}
                               className="h-8 text-[12px]"
                               data-testid={`crq-line-name-${i}`}/>
                      </td>
                      <td className="py-1.5 pr-2 text-right">
                        <Input type="number" min={0} value={it.qty}
                               onChange={(e) => setItems(arr => arr.map((x, ix) => ix === i ? { ...x, qty: Number(e.target.value) || 0 } : x))}
                               className="h-8 w-20 text-right tabular-nums text-[12px]"
                               data-testid={`crq-line-qty-${i}`}/>
                        {qtyDropped && <div className="text-[9.5px] text-amber-700 mt-0.5 mono uppercase tracking-wider">Short-ship</div>}
                      </td>
                      <td className="py-1.5 pr-2 text-right">
                        <Input type="number" min={0} step="0.01" value={it.unit_price}
                               onChange={(e) => setItems(arr => arr.map((x, ix) => ix === i ? { ...x, unit_price: Number(e.target.value) || 0 } : x))}
                               className="h-8 w-24 text-right tabular-nums text-[12px]"
                               data-testid={`crq-line-price-${i}`}/>
                      </td>
                      <td className="py-1.5 text-right tabular-nums font-medium">{money((it.qty || 0) * (it.unit_price || 0))}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="border-t border-border">
                <tr className="text-[12px]">
                  <td colSpan={4} className="pt-2 text-right text-foreground/65">Original total</td>
                  <td className="pt-2 text-right tabular-nums">{money(originalTotal)}</td>
                </tr>
                <tr className="text-[12px]">
                  <td colSpan={4} className="text-right text-foreground/65">Proposed total</td>
                  <td className="text-right tabular-nums font-semibold">{money(proposedTotal)}</td>
                </tr>
                <tr className={`text-[12px] ${refundDelta > 0 ? "text-emerald-700" : "text-foreground/55"}`}>
                  <td colSpan={4} className="text-right">{refundDelta > 0 ? "Refund to buyer wallet" : "No refund"}</td>
                  <td className="text-right tabular-nums font-medium">{money(Math.max(0, refundDelta))}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Auto-detected kind */}
          <div className="flex items-center gap-2 text-[12px]">
            <span className="text-foreground/55">Change type:</span>
            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] mono uppercase tracking-wider ${
              kind === "substitute" ? "bg-blue-500/15 text-blue-700" :
              kind === "mixed"      ? "bg-amber-500/15 text-amber-800" :
                                       "bg-emerald-500/15 text-emerald-700"
            }`} data-testid="crq-kind-pill">
              {kind === "substitute" && <Replace className="w-3 h-3" />}
              {kind === "mixed"      && <AlertTriangle className="w-3 h-3" />}
              {kind === "short_ship" && <Package className="w-3 h-3" />}
              {kind.replace(/_/g, " ")}
            </span>
            <span className="text-[11px] text-foreground/45">auto-detected from your edits</span>
          </div>

          {/* Reason */}
          <div>
            <label className="block text-[10px] mono uppercase tracking-widest text-foreground/55 mb-1.5">
              Reason for change <span className="text-rose-700 normal-case">*</span>
              <span className="text-[10px] normal-case text-foreground/45 ml-2">required · ≥10 chars · buyer will see this</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. 3 units of SKU-A-LAVENDER were damaged during pick. Substituting with SKU-A-LAVENDER-V2 (same product, newer formulation, same MSRP)."
              className="w-full min-h-[80px] rounded-lg border border-border bg-white px-3 py-2 text-[12.5px] focus:outline-none focus:border-foreground/40"
              data-testid="crq-reason"
            />
            <div className="text-[10.5px] text-foreground/45 mt-1">
              Be specific — the buyer's decision depends on understanding what's actually changing.
            </div>
          </div>

          {error && (
            <div className="rounded-lg border border-rose-300 bg-rose-50/50 p-3 text-[12px] text-rose-900 flex items-start gap-2" data-testid="crq-error">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              {error}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            onClick={send}
            disabled={busy || exceeds || !reason.trim()}
            className="bg-foreground text-background"
            data-testid="crq-send"
          >
            {busy ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin"/>Sending…</> : <>Send change request</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Buyer-side banner + decision UI
// ─────────────────────────────────────────────────────────────────────────────
export function BuyerChangeRequestBanner({ crq, onDecided }) {
  const [busy, setBusy]     = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [comment, setComment] = useState("");

  if (!crq || crq.status !== "pending") return null;

  const refundDelta = Number(crq.refund_delta || 0);
  const expiresIn   = (() => {
    if (!crq.expires_at) return "";
    const ms = new Date(crq.expires_at).getTime() - Date.now();
    if (ms <= 0) return "expired";
    const days = Math.ceil(ms / 86_400_000);
    return `${days}d left`;
  })();

  const decide = async (action) => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/buyer/change-requests/${crq.id}/${action}`,
        { reason: comment.trim() || null },
      );
      const verb = action === "accept" ? "Accepted" : action === "decline" ? "Declined" : "Cancelled";
      toast.success(`${verb} · ${refundDelta > 0 && action !== "decline" ? `${money(refundDelta)} refund processing` : "saved"}`);
      onDecided?.(action, data);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Couldn't save");
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border-2 border-amber-400/60 bg-amber-50/60 p-5 space-y-3" data-testid="buyer-crq-banner">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-[13.5px] font-semibold text-amber-900">
            {crq.seller_name} needs your approval on this order
          </div>
          <div className="text-[11.5px] text-amber-900/80 mt-0.5 inline-flex items-center gap-1.5">
            <Clock className="w-3 h-3" />
            {expiresIn} · auto-cancels with full refund if you don't respond
          </div>
        </div>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] mono uppercase tracking-wider border ${
          crq.kind === "substitute" ? "bg-blue-500/15 text-blue-700 border-blue-500/20" :
          crq.kind === "mixed"      ? "bg-amber-500/15 text-amber-800 border-amber-500/30" :
                                       "bg-emerald-500/15 text-emerald-700 border-emerald-500/20"
        }`}>
          {(crq.kind || "").replace(/_/g, " ")}
        </span>
      </div>

      {/* Reason */}
      <div className="rounded-lg bg-white border border-amber-200 p-3 text-[12px] text-foreground/85">
        <div className="text-[9.5px] mono uppercase tracking-widest text-foreground/55 mb-1">Seller's reason</div>
        {crq.reason}
      </div>

      {/* Before / After diff */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-[11.5px]">
        <div className="rounded-lg bg-white border border-border p-3">
          <div className="text-[9.5px] mono uppercase tracking-widest text-foreground/55 mb-1.5">You ordered</div>
          {(crq.original_items || []).map((it, i) => (
            <div key={i} className="flex justify-between gap-2 py-0.5">
              <span className="truncate text-foreground/85">
                {it.qty}× <span className="font-mono text-[10.5px]">{it.sku || "—"}</span> {it.name}
              </span>
              <span className="tabular-nums text-foreground/60 shrink-0">{money((it.qty || 0) * (it.unit_price || it.price || 0))}</span>
            </div>
          ))}
          <div className="border-t border-border/60 mt-1.5 pt-1.5 flex justify-between font-semibold">
            <span>Total</span><span className="tabular-nums">{money(crq.original_total)}</span>
          </div>
        </div>
        <div className="rounded-lg bg-white border border-amber-300 p-3">
          <div className="text-[9.5px] mono uppercase tracking-widest text-amber-800 mb-1.5">Seller proposes</div>
          {(crq.proposed_items || []).map((it, i) => (
            <div key={i} className="flex justify-between gap-2 py-0.5">
              <span className="truncate text-foreground/85">
                {it.qty}× <span className="font-mono text-[10.5px]">{it.sku || "—"}</span> {it.name}
                {it.substitute_for_sku && <span className="ml-1 text-[9.5px] mono uppercase tracking-wider text-blue-700">subbed for {it.substitute_for_sku}</span>}
              </span>
              <span className="tabular-nums text-foreground/60 shrink-0">{money((it.qty || 0) * (it.unit_price || 0))}</span>
            </div>
          ))}
          <div className="border-t border-border/60 mt-1.5 pt-1.5 flex justify-between font-semibold">
            <span>Total</span><span className="tabular-nums">{money(crq.proposed_total)}</span>
          </div>
          {refundDelta > 0 && (
            <div className="mt-1.5 text-[11px] text-emerald-700 font-medium">
              You'll receive {money(refundDelta)} back to your wallet if you accept
            </div>
          )}
        </div>
      </div>

      {/* Optional comment for the seller */}
      <div>
        <label className="text-[10px] mono uppercase tracking-widest text-foreground/55 block mb-1">Optional note for the seller</label>
        <Input
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="e.g. OK to substitute — newer formulation is fine for our customers"
          className="text-[12px]"
          data-testid="crq-buyer-comment"
        />
      </div>

      {/* Decision actions */}
      {!showCancel ? (
        <div className="flex flex-wrap gap-2 pt-1">
          <Button
            size="sm"
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={() => decide("accept")}
            disabled={busy}
            data-testid="crq-buyer-accept"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1.5" />}
            Accept change{refundDelta > 0 ? ` · ${money(refundDelta)} refund` : ""}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => decide("decline")}
            disabled={busy}
            data-testid="crq-buyer-decline"
          >
            <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
            Decline · ship original if possible
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="text-rose-700 hover:bg-rose-50"
            onClick={() => setShowCancel(true)}
            disabled={busy}
            data-testid="crq-buyer-cancel-toggle"
          >
            <X className="w-3.5 h-3.5 mr-1.5" />
            Cancel order
          </Button>
        </div>
      ) : (
        <div className="rounded-lg border border-rose-300 bg-rose-50/40 p-3 space-y-2 text-[12px]">
          <div className="text-rose-900">
            <strong>Cancel the entire order?</strong> This voids the invoice and refunds the full amount ({money(crq.original_total)}) to your wallet.
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" className="bg-rose-700 text-white" onClick={() => decide("cancel-order")} disabled={busy} data-testid="crq-buyer-cancel-confirm">
              {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <X className="w-3.5 h-3.5 mr-1.5" />}
              Yes, cancel order
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setShowCancel(false)} disabled={busy}>Keep it open</Button>
          </div>
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Seller-side status pill — shows on the seller's order row when a CRQ is open
// ─────────────────────────────────────────────────────────────────────────────
export function CrqStatusPill({ crq }) {
  if (!crq) return null;
  const map = {
    pending:              { label: "Awaiting buyer",            tone: "bg-amber-500/15 text-amber-800 border-amber-500/30" },
    accepted:             { label: "Buyer accepted",            tone: "bg-emerald-500/15 text-emerald-700 border-emerald-500/30" },
    declined:             { label: "Buyer declined",            tone: "bg-foreground/10 text-foreground/65 border-foreground/15" },
    order_cancelled:      { label: "Buyer cancelled order",     tone: "bg-rose-500/15 text-rose-700 border-rose-500/30" },
    expired_auto_cancelled: { label: "Auto-cancelled · expired", tone: "bg-rose-500/15 text-rose-700 border-rose-500/30" },
  };
  const m = map[crq.status] || { label: crq.status, tone: "bg-foreground/10 text-foreground/65 border-foreground/15" };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] mono uppercase tracking-wider border ${m.tone}`}>
      <Clock className="w-2.5 h-2.5" />
      {m.label}
    </span>
  );
}
